<template>
  <div
    v-if="financingRate > 0"
    class="financing-calculator"
    :class="{
      [`financing-calculator--${visual}`]: visual,
      'financing-calculator--isCollapsable': isCollapsable,
      'financing-calculator--collapsed': collapsed && isCollapsable,
    }"
  >
    <div class="financing-calculator__logo">
      <CdnImage
        render-without-figure
        :src="logo"
        alt="Finanzierung"
      />
    </div>

    <div class="financing-calculator__title">
      <slot name="title">
        <strong>Jetzt smart finanzieren mit Ratenzahlung und Wunschlaufzeit!</strong>
      </slot>
    </div>

    <div class="financing-calculator__description">
      <slot name="description">
        Teste bequem, welche Monatsrate für dich passt – die finale Auswahl triffst du ganz
        unkompliziert beim Bezahlvorgang.
      </slot>
    </div>

    <LinkButton
      v-if="isCollapsable"
      class="financing-calculator__collapser"
      tag="button"
      type="button"
      text
      @click="collapsed = !collapsed"
    >
      <span v-if="collapsed">Rate berechnen</span>
      <span v-else>ausblenden</span>
    </LinkButton>

    <div class="financing-calculator__select">
      <slot>
        <RadioGroup
          v-model="durationMonth"
          name="rate"
          :options="options"
          display-error-message
          class="mail-form__checkbox"
        />
      </slot>
    </div>

    <div class="financing-calculator__rate-title">
      <slot name="rate-title"><strong>Deine Monatsrate:</strong></slot>
    </div>

    <div class="financing-calculator__rate">
      <slot name="rate">{{ humanizePriceFromCents(financingRate) }}</slot>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, type PropType, ref } from 'vue';
import { humanizePriceFromCents } from '@i22/rocket';
import { RadioGroup, LinkButton } from '@i22/td-longtail-vue-components';
import CdnImage from '@/components/cdn-image.vue';

type Radio = {
  value: string;
  label: string;
};
const props = defineProps({
  annualRate: { type: Number, default: 0 }, // in percent 0 - 100
  price: { type: Number, default: 0 }, // in cent
  options: {
    type: Array as PropType<Radio[]>,
    default: () => [
      { label: '3 Monate', value: '3' },
      { label: '6 Monate', value: '6' },
      { label: '12 Monate', value: '12' },
    ],
  },
  duration: { type: String, default: '12' },
  visual: {
    type: String,
    validator: (val: string) => ['cart', 'product-page', 'payment'].includes(val),
    default: 'cart',
  },
  isCollapsable: { type: Boolean },
});

const collapsed = ref(true);

const durationMonth = ref<string>(props.duration || '12');

const logo = computed(() => {
  if (props.annualRate === 0)
    return '/content/content-images/payment/b17961ca37fce4b78fd7c3d89f7eb9f2/installment_rate_0.svg';
  return '/content/content-images/payment/860ae02eedc4af58cb1e12ed4a50bd75/installment.svg';
});

const financingRate = computed(() => {
  if (props.annualRate === 0) {
    return Math.ceil(props.price / parseInt(durationMonth.value, 10));
  }

  const r = props.annualRate / 12 / 100; // Convert annual rate to monthly rate as a decimal
  const monthlyPayment = (r * props.price) / (1 - (1 + r) ** -durationMonth.value);

  return Math.ceil(monthlyPayment);
});

defineExpose({ financingRate });
</script>

<style lang="scss" scoped>
@use 'assets/base' as *;
.financing-calculator {
  display: grid;
  grid-gap: $grid-base;
  grid-template-areas: 'logo title select rate';

  &--product-page,
  &--cart {
    border: 1px solid #d0d0d0;
    border-radius: 6px;
    padding: $grid-base;

    .financing-calculator {
      &__logo {
        @include mq(false, $mq-md - 1) {
          margin-top: -22px;
          width: 50px;
        }
      }
    }
  }

  &--cart {
    margin-block: $grid-base;
    grid-template-areas:
      'logo collapser'
      'title title'
      'description description'
      'select rateTitle'
      'select rate';

    @include mq($mq-md) {
      grid-template-areas:
        'logo title title collapser'
        '. description description description'
        '. select rateTitle rate';
      grid-template-columns: 40px auto auto auto auto;
      align-items: center;
    }

    :deep(.radio-group__container) {
      @include mq($mq-md) {
        flex-direction: row;
      }
    }

    .financing-calculator {
      &__rate {
        @include mq($mq-md) {
          align-self: center;
        }
      }
      &__rate-title {
        @include mq(false, $mq-md - 1) {
          text-align: right;
          justify-self: end;
          align-self: end;
        }
      }
    }
  }

  &--product-page {
    grid-template-areas:
      'logo collapser'
      'title title'
      'description description'
      'select rateTitle'
      'select rate';
    margin-right: $grid-base;

    @include mq($mq-md) {
      grid-template-areas:
        'logo title title'
        'logo description collapser'
        '. select select'
        '. rateTitle rate';
      grid-template-columns: 40px auto max-content max-content max-content;
    }

    :deep(.radio-group__container) {
      @include mq($mq-md) {
        flex-direction: row;
      }
    }

    .financing-calculator {
      &__rate-title {
        justify-self: end;
        align-self: end;
      }
    }
  }

  &--payment {
    margin-inline: $grid-base;
    margin-bottom: $grid-base * 3;
    grid-template-areas:
      'title title'
      'select select'
      'rateTitle rate'
      'description description';

    .financing-calculator {
      &__logo {
        display: none;
      }
    }
  }

  &__logo {
    grid-area: logo;
  }

  &__title {
    grid-area: title;
  }

  &__description {
    grid-area: description;
  }

  &__rate {
    grid-area: rate;
    border: 1px solid #d0d0d0;
    border-radius: 3px;
    background-color: #f8f8f8;
    text-align: right;
    color: var(--color-primary);
    font-weight: 700;
    padding: 2px $grid-base * 0.5;
    align-self: flex-start;
    justify-self: end;
    min-width: 80px;
  }
  &__rate-title {
    grid-area: rateTitle;
  }

  &__select {
    grid-area: select;
  }
  &__collapser {
    grid-area: collapser;
    padding: 0;
    justify-self: end;
    &:after {
      content: '';
      display: inline-block;
      border: 0 solid currentColor;
      border-right-width: 1px;
      border-top-width: 1px;
      width: 0.4em;
      height: 0.4em;
      transform: rotate(315deg);
      transition: transform 0.25s;
      transform-origin: center center;
      margin-left: 0.5em;
    }

    &:focus {
      outline: none;
    }
  }

  &:not(&--isCollapsable) {
    .financing-calculator__collapser {
      display: none;
    }
  }

  &--isCollapsable {
    .financing-calculator {
      &__select,
      &__description,
      &__rate-title,
      &__rate {
        transition: all 0.25s;
      }
    }
  }

  &--collapsed {
    &.financing-calculator {
      padding-bottom: 0;
    }
    .financing-calculator {
      &__select,
      &__description,
      &__rate-title,
      &__rate {
        opacity: 0;
        margin-top: -120px;
        pointer-events: none;
        @include mq($mq-md) {
          margin-top: -50px;
        }
      }

      &__collapser:after {
        transform: rotate(135deg) translateY(5px);
      }
    }
  }
}
</style>
